import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";

import ListPage from "ui/ListPage";
import ConfirmDialog from "ui/ConfirmDialog";
import SnackbarAlert from "ui/SnackbarAlert";

import {
  listReconocimientos,
  deleteReconocimiento,
} from "../services/reconocimientos";

import buildTable, { columns as recogCols } from "./datosreconocimientos";

const toArray = (v) => (Array.isArray(v) ? v : []);

export default function Reconocimientos() {
  const navigate = useNavigate();

  const [search, setSearch] = useState("");
  const [items, setItems]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr]       = useState("");

  const [pending, setPending] = useState(null);
  const [snack, setSnack] = useState({ open: false, message: "", severity: "success" });
  const openSnack = (m, s="success") => setSnack({ open: true, message: m, severity: s });
  const closeSnack = () => setSnack((s) => ({ ...s, open: false }));

  useEffect(() => {
    let cancel = false;
    (async () => {
      setLoading(true); setErr("");
      try {
        const list = await listReconocimientos();
        if (!cancel) setItems(list);
      } catch (e) {
        if (!cancel) setErr(e?.response?.data?.message || e.message || String(e));
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => { cancel = true; };
  }, []);

  const { columns, rows } = useMemo(
    () =>
      buildTable(toArray(items), (it) => {
        const id = it.id ?? it._id;
        const nombre = it.name ?? it.nombre ?? "-";
        const tipo = it.type ?? it.tipo ?? "-";
        return {
          id,
          nombre,
          tipo,
          creado: it.created_at ?? it.createdAt,
          onEdit: () => navigate(`/catalogos/reconocimientos/${id}/editar`),
          onDelete: () => setPending({ id, nombre }),
        };
      }),
    [items, navigate]
  );

  const getText = (row) =>
    typeof row?.reconocimiento === "string"
      ? row.reconocimiento
      : row?.reconocimiento?.props?.children || "";

  const filteredRows = useMemo(() => {
    if (!search) return rows;
    const s = search.toLowerCase();
    return rows.filter((r) => String(getText(r)).toLowerCase().includes(s));
  }, [rows, search]);

  const confirmDelete = async () => {
    if (!pending) return;
    try {
      await deleteReconocimiento(pending.id);
      setItems((prev) => prev.filter((x) => (x.id ?? x._id) !== pending.id));
      openSnack(`Eliminado: ${pending.nombre}`, "success");
    } catch (e) {
      const msg = e?.response?.data?.message || e.message || String(e);
      setErr(msg); openSnack(`No se pudo eliminar: ${msg}`, "error");
    } finally {
      setPending(null);
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />

      <ListPage
        title="Reconocimientos"
        search={search}
        onSearchChange={setSearch}
        addText="Agregar"
        onAdd={() => navigate("/catalogos/reconocimientos/nuevo")}
        columns={recogCols}
        rows={filteredRows}
        loading={loading}
        error={err}
        cardSx={{ maxWidth: "100%", mx: 0 }}
      />

      <Footer />

      <ConfirmDialog
        open={!!pending}
        title="Eliminar reconocimiento"
        description={`¿Seguro que quieres eliminar "${pending?.nombre}"?`}
        confirmText="Eliminar"
        onClose={() => setPending(null)}
        onConfirm={confirmDelete}
      />

      <SnackbarAlert open={snack.open} onClose={closeSnack} message={snack.message} severity={snack.severity} />
    </DashboardLayout>
  );
}
